import type { KeyboardEvent, MouseEvent, ReactNode } from "react";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { Country } from "react-phone-number-input/max";

import { ExistingAccount } from "@ui/components/ExistingAccount/ExistingAccount";
import { useFormContext } from "@ui/components/Form";
import {
  OtpChannelSelector,
  OtpInputSection,
  PhoneVerificationMessage,
  ResendCodeSection,
} from "@ui/components/OtpComponent/components";
import { Telephone } from "@ui/components/Telephone/Telephone";
import { useCounters } from "@ui/hooks/useCounters/useCounters";
import { usePhoneNumberFromAv } from "@ui/hooks/usePhoneNumberFromAv";
import { useTranslations } from "@ui/providers/useTranslations";
import { validationPatterns } from "@ui/utils/validationPatterns";
import classNames from "classnames";

import { useConfig } from "@jti/app-context/useConfig";
import { Button } from "@jti/core-components/components/Button";
import { Separator } from "@jti/core-components/components/Separator";
import { toast } from "@jti/core-components/components/Toaster";
import type { OtpChannel } from "@jti/fe-common";
import {
  getErrorMessageFromError,
  isBackendValidationError,
  mapBackendErrorToFormFields,
  markServerErrorsAndScroll,
  translateBackendErrorCode,
} from "@jti/fe-common";

import styles from "./OtpComponent.module.scss";

type CheckPhoneError = {
  statusCode: number;
  message: string;
  error_code: string;
  attributes?: {
    phone?: Record<string, boolean>;
  };
};

const isCheckPhoneError = (error: unknown): error is CheckPhoneError =>
  typeof error === "object" &&
  error !== null &&
  "statusCode" in error &&
  typeof error.statusCode === "number" &&
  "message" in error &&
  typeof error.message === "string" &&
  "error_code" in error &&
  typeof error.error_code === "string";

type OtpComponentVariant = "login" | "registration" | "account_information";

export interface OtpComponentProps {
  inputStyle?: string;
  dataTestId?: string;
  fieldName?(name: string): string;
  isInputNum?: boolean;
  onSendCode: (channel: OtpChannel) => void;
  onCheckPhone: (phone: string) => Promise<{ statusCode: number } | CheckPhoneError>;
  error?: string;
  isShowBorder?: boolean;
  isShowSeparator?: boolean;
  isOTPenabled?: boolean;
  isPhoneRequired?: boolean;
  isCounterEnabled?: boolean;
  time?: number;
  attemptsNum?: number;
  isAttemptError?: boolean;
  onSubmitOTP: () => void;
  isPhoneValidatedViaSms?: boolean;
  autocompleteType?: string;
  isSendCodeOnlyForExistingAccount?: boolean;
  resendBtnNextLine?: boolean;
  sendCodeBtnText?: string;
  className?: string;
  isLoginButtonDisabled?: boolean;
  isSendCodeDisabled?: boolean;
  isPhoneValidated?: boolean;
  isPhoneFieldFullWidth?: boolean;
  children?: ReactNode;
  variant: OtpComponentVariant;
  isAutoSendEnabled?: boolean;
  onPhoneValidated?: () => void;
  defaultCountry?: Country;
  forceInternationalPhoneNumber?: boolean;
}

const CONSTANTS = {
  DEFAULT_TIME: 10,
  TELEPHONE_PREFIX: "editPhone",
} as const;

const NUMBER_OF_OTP_INPUTS = 4;

const statusCodes = {
  SUCCESS: 200,
  NOT_FOUND: 404,
  VALIDATION_ERROR: 422,
} as const;

const handleEnter = (cb: (e: KeyboardEvent) => void) => (e: KeyboardEvent) => {
  if (e.key === "Enter" && !e.isDefaultPrevented()) {
    cb(e);
  }
};

export const OtpComponent = memo(
  ({
    inputStyle = "",
    dataTestId = "otp",
    fieldName = (name: string) => name,
    isInputNum = false,
    onSendCode,
    onCheckPhone,
    isShowBorder = false,
    isShowSeparator = false,
    isOTPenabled = true,
    isPhoneRequired = false,
    isCounterEnabled = false,
    time = CONSTANTS.DEFAULT_TIME,
    onSubmitOTP,
    isPhoneValidatedViaSms = false,
    autocompleteType = "off",
    isSendCodeOnlyForExistingAccount = false,
    isAttemptError = false,
    resendBtnNextLine = false,
    attemptsNum = 0,
    sendCodeBtnText,
    className,
    isLoginButtonDisabled = false,
    isSendCodeDisabled = false,
    isPhoneValidated = false,
    isPhoneFieldFullWidth,
    children,
    variant,
    isAutoSendEnabled = false,
    defaultCountry,
    forceInternationalPhoneNumber = false,
  }: OtpComponentProps) => {
    const { __, dictionary } = useTranslations();
    const [isAccountExist, setIsAccountExist] = useState(false);
    const [isCodeSent, setIsCodeSent] = useState(isPhoneValidated);
    const [isSendingCode, setIsSendingCode] = useState(false);

    const {
      REGISTER_PHONE_REQUIRED,
      isInternationalPhoneEnabled: isInternationalPhoneEnabledConfig,
      isOtpSelectionEnabled = false,
      defaultOtpChannel = "sms",
      otpOptions = {},
      isPhoneEditable,
    } = useConfig();

    const isInternationalPhoneEnabled = forceInternationalPhoneNumber || isInternationalPhoneEnabledConfig;

    const wrapperRef = useRef<HTMLDivElement>(null);

    const hasAutoSubmitted = useRef(false);
    const lastAutoSubmittedCode = useRef<string | null>(null);

    const [otpChannel, setOtpChannel] = useState<OtpChannel>(defaultOtpChannel);

    const { remainingTime, resetTimer, clearTimer } = useCounters(time);
    const form = useFormContext();
    const {
      watch,
      setValue,
      clearErrors,
      formState: { errors },
    } = form;
    const telephonePrefix = CONSTANTS.TELEPHONE_PREFIX;
    const phoneFieldName = fieldName(
      `${telephonePrefix}.${isInternationalPhoneEnabled ? "internationalPhone" : "phone"}`
    );
    const { value } = watch(`${telephonePrefix}.code`) || {};
    const phone: string = watch(fieldName(phoneFieldName));
    const code: string = watch(fieldName("phone_validation_code"));
    const telephoneFieldName = fieldName("telephone");
    const { isAutopopulated } = usePhoneNumberFromAv(fieldName(phoneFieldName));

    useEffect(() => {
      if (!isAutoSendEnabled) return;

      if (code !== lastAutoSubmittedCode.current) {
        hasAutoSubmitted.current = false;
      }
    }, [code, isAutoSendEnabled]);

    useEffect(() => {
      if (!isAutoSendEnabled) return;

      if (code?.length === NUMBER_OF_OTP_INPUTS && !hasAutoSubmitted.current) {
        hasAutoSubmitted.current = true;
        lastAutoSubmittedCode.current = code;
        onSubmitOTP();
      }
    }, [code, isAutoSendEnabled, onSubmitOTP]);

    useEffect(() => {
      if (isInternationalPhoneEnabled && phone) {
        setValue(telephoneFieldName, phone.replace(validationPatterns.SPACES, ""));
      } else if (value && phone) {
        setValue(telephoneFieldName, `${value}${phone.replace(validationPatterns.SPACES, "")}`);
      } else {
        setValue(telephoneFieldName, "");
      }
    }, [value, phone, setValue, telephoneFieldName, isInternationalPhoneEnabled]);

    useEffect(() => {
      if (isPhoneValidatedViaSms && isCounterEnabled) {
        clearTimer();
      }
    }, [clearTimer, isPhoneValidatedViaSms, isCounterEnabled]);

    const sendCode = useCallback(
      (event: MouseEvent<HTMLElement> | KeyboardEvent<Element>) => {
        event.preventDefault();
        setIsSendingCode(true);
        onSendCode(otpChannel);
        resetTimer();
        setIsSendingCode(false);
      },
      [onSendCode, resetTimer, otpChannel]
    );

    const handleEditPhone = useCallback(() => {
      setIsCodeSent(false);
      setIsAccountExist(false);
      clearTimer();
      clearErrors(fieldName("phone_validation_code"));
      setValue(fieldName("phone_validation_code"), "");
      hasAutoSubmitted.current = false;
      lastAutoSubmittedCode.current = null;
    }, [clearErrors, clearTimer, fieldName, setValue]);

    const handleSendCode = useCallback(
      async (event: MouseEvent<HTMLElement> | KeyboardEvent<Element>) => {
        if (isCodeSent || !phone) return;

        try {
          const trimmedPhone = phone.replace(validationPatterns.SPACES, "");
          const formattedPhone = isInternationalPhoneEnabled ? trimmedPhone : `${value}${trimmedPhone}`;
          const { statusCode } = await onCheckPhone(formattedPhone);

          if (statusCode === statusCodes.SUCCESS) {
            setIsAccountExist(!isSendCodeOnlyForExistingAccount);
            if (isSendCodeOnlyForExistingAccount) {
              setIsCodeSent(true);
              sendCode(event);
            }
          }
        } catch (err) {
          const checkPhoneError = isCheckPhoneError(err) ? err : undefined;
          const { statusCode, error_code, attributes } = checkPhoneError ?? {};

          const isConsumerNotFound = isSendCodeOnlyForExistingAccount && statusCode === statusCodes.NOT_FOUND;
          const isValidationError = statusCode === statusCodes.VALIDATION_ERROR;
          const isPhoneVerificationError = isValidationError && attributes?.phone;

          // For registration (isSendCodeOnlyForExistingAccount=false): proceed on any non-422 error
          // (e.g. 404 means phone not yet registered — allow to continue).
          // For login (isSendCodeOnlyForExistingAccount=true): never fall through on unknown errors
          // (e.g. 5xx) — we cannot confirm the account exists, so show an error instead.
          if (!isSendCodeOnlyForExistingAccount && !isValidationError) {
            setIsAccountExist(false);
            setIsCodeSent(true);
            sendCode(event);
            return;
          }

          const errorMessageKey = isConsumerNotFound
            ? "users.profile::consumer_not_found"
            : "users.authentication::authenticate.validation_error";
          const errorMessage = isPhoneVerificationError
            ? getErrorMessageFromError(err, checkPhoneError?.message ?? "")
            : translateBackendErrorCode(error_code, __(errorMessageKey), dictionary);

          toast.error(errorMessage);

          if (variant !== "login") {
            const backendError = isBackendValidationError(err) ? err : undefined;
            const formFieldNames = mapBackendErrorToFormFields(backendError, { phone: phoneFieldName });
            markServerErrorsAndScroll(form, formFieldNames, wrapperRef);
          }
        }
      },
      [
        isCodeSent,
        phone,
        errors,
        isInternationalPhoneEnabled,
        value,
        onCheckPhone,
        isSendCodeOnlyForExistingAccount,
        sendCode,
        __,
        variant,
        phoneFieldName,
        form,
      ]
    );

    const handleSubmitOTP = (e: KeyboardEvent | MouseEvent<HTMLButtonElement>): void => {
      if (code.length < NUMBER_OF_OTP_INPUTS) return;
      e.preventDefault();
      onSubmitOTP();
    };

    const isCounterTicking = isCounterEnabled && Boolean(remainingTime) && !isPhoneValidated;
    const isTelephoneRequired = isOTPenabled || isPhoneRequired || JSON.parse(REGISTER_PHONE_REQUIRED || "false");

    const disablePhoneInputOnEdit = !isPhoneEditable && variant === "account_information";

    const isTelephoneDisabled =
      disablePhoneInputOnEdit || isPhoneValidatedViaSms || isAutopopulated || isPhoneValidated;

    const isTelephoneEditable = isCodeSent && !isTelephoneDisabled;

    const telephoneProps = useMemo(
      () => ({
        fieldName: (name: string) => `${telephonePrefix}.${name}`,
        isRequired: isTelephoneRequired,
        isDisabled: isTelephoneDisabled,
        isInfoMessageVisible: true,
        isPhoneFieldFullWidth,
      }),
      [isTelephoneRequired, isTelephoneDisabled, isPhoneFieldFullWidth, telephonePrefix]
    );

    const isLoginBtnDisabled = code?.length < NUMBER_OF_OTP_INPUTS || isLoginButtonDisabled;
    const isOtpInputDisabled = isPhoneValidated || (isSendCodeOnlyForExistingAccount && attemptsNum === 0);
    const isUpdateBtnDisabled = code?.length !== NUMBER_OF_OTP_INPUTS;

    const shouldShowLoginButton = variant === "login" && !isAutoSendEnabled;

    return (
      <div
        className={classNames(styles.otpWrapper, { [styles.fullWidth]: isPhoneFieldFullWidth }, className)}
        data-testid="otpComponent"
        ref={wrapperRef}
      >
        <div onKeyDown={handleEnter(handleSendCode)}>
          <Telephone
            {...telephoneProps}
            disclaimer={__("internationalPhone.input.description")}
            defaultCountry={defaultCountry}
            forceInternationalPhoneNumber={forceInternationalPhoneNumber}
            autoComplete="tel-national"
            mode={isTelephoneEditable ? "editable" : "standard"}
            onEditClick={handleEditPhone}
          />
        </div>

        <PhoneVerificationMessage isVisible={isPhoneValidatedViaSms} className={styles.phoneVerifiedAlert} />

        {isOTPenabled && !disablePhoneInputOnEdit && (
          <>
            {isOtpSelectionEnabled && !isPhoneValidatedViaSms && (
              <OtpChannelSelector
                defaultChannel={defaultOtpChannel}
                isDisabled={isTelephoneDisabled}
                otpOptions={otpOptions}
                onChannelChange={setOtpChannel}
              />
            )}

            {!isPhoneValidatedViaSms && (
              <>
                {!isCodeSent && (
                  <div className={styles.text}>{__("profile.phone_update_page.otp.verification_text")}</div>
                )}
                {children}
                <Button
                  data-testid="sendCode"
                  variant="primary"
                  onClick={handleSendCode}
                  disabled={!phone || isCodeSent || Boolean(Object.keys(errors)?.length) || isSendCodeDisabled}
                  pending={isSendingCode}
                >
                  {isCodeSent
                    ? __("profile.phone_update_page.otp.button_send_text")
                    : sendCodeBtnText || __("profile.phone_update_page.otp.button_text")}
                </Button>
              </>
            )}

            {isAccountExist && <ExistingAccount />}

            {!isAccountExist && isCodeSent && !isPhoneValidatedViaSms && (
              <>
                {isShowSeparator && (
                  <div className={styles.separator}>
                    <Separator />
                  </div>
                )}
                <div
                  className={classNames(styles.innerWrapper, { [styles.border]: isShowBorder })}
                  onKeyDown={handleEnter(handleSubmitOTP)}
                >
                  <div className={styles.text}>{__("profile.phone_update_page.otp.expire_text")}</div>
                  <OtpInputSection
                    fieldName={fieldName}
                    dataTestId={dataTestId}
                    inputStyle={inputStyle}
                    isInputNum={isInputNum}
                    autocompleteType={autocompleteType}
                    isAttemptError={isAttemptError}
                    attemptsNum={attemptsNum}
                    isDisabled={isOtpInputDisabled}
                  />
                  {shouldShowLoginButton && (
                    <Button
                      className={styles.otpSubmitButton}
                      variant="primary"
                      disabled={isLoginBtnDisabled}
                      data-testid="otpLoginButton"
                      onClick={handleSubmitOTP}
                    >
                      {__("login.login_button_label")}
                    </Button>
                  )}
                  {variant === "account_information" && (
                    <Button
                      className={styles.otpSubmitButton}
                      variant="primary"
                      disabled={isUpdateBtnDisabled}
                      onClick={handleSubmitOTP}
                    >
                      {__("profile.phone_update.dialog.update_btn")}
                    </Button>
                  )}
                  <ResendCodeSection
                    isCounterTicking={isCounterTicking}
                    remainingTime={remainingTime}
                    isDisabled={isSendCodeOnlyForExistingAccount && attemptsNum === 0}
                    resendBtnNextLine={resendBtnNextLine}
                    onResend={sendCode}
                  />
                </div>
              </>
            )}
          </>
        )}
      </div>
    );
  }
);
